import {
  HttpEvent,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { from, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { IdentityService } from './identity.service';
import { Config } from '../config/config.service';

const PUBLIC_URL_PATTERNS = ['promotions', 'winners', 'PoolSeasonStats'];
// The user's own login/logout/account-recovery attempt — these show their own error UI, so a
// 401 here cleans up locally without the generic expiry redirect
const EXPLICIT_AUTH_ACTION_PATTERN =
  /sign_in$|sign_out$|unlock_account$|reset_password$/;
// The generic microsite session-health-check — its failure says nothing about whether the
// wallet's own Devise identity is valid, so it must never clear a different, valid identity
const MICROSITE_SESSION_CHECK_PATTERN = /sessions$|sessions\?/;
// Devise Token Auth headers a retry must clear first, since clone()'s setHeaders only overwrites keys it's given
const DEVISE_IDENTITY_HEADER_KEYS = ['access-token', 'client', 'uid', 'expiry'];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const identity = inject(IdentityService);
  const config = inject(Config);

  if (config.CONFIG.enableNativeHttp) return next(req);

  // Tracking beacons carry no identity (fe-tracking only reads query params). Any of the
  // default/identity headers would turn each beacon into a non-simple CORS request and
  // cost one OPTIONS preflight per beacon, so send them untouched.
  const trackingUrl = config.CONFIG.trackingUrl;
  if (trackingUrl && req.url.startsWith(trackingUrl)) return next(req);

  // Every request — public or not — goes through the same response/error handling
  // (matches old behavior: promotions/winners/PoolSeasonStats still ran through the
  // shared map()/catchError() pipe in the old class-based interceptor).
  const handleResponse = (
    authReq: HttpRequest<any>,
    isPublicRequest: boolean,
  ): Observable<HttpEvent<any>> =>
    next(authReq).pipe(
      map((event) => {
        if (event instanceof HttpResponse) {
          identity.updateIdentityFromResponse(event.headers, req.url);
        }
        return event;
      }),
      catchError((err) => {
        if (err.status !== 401) {
          return throwError(() => identity.errorHandler(err));
        }

        if (isPublicRequest) {
          // Public requests never carry identity — a 401 here says nothing about the user's session
          return throwError(() => identity.errorHandler(err));
        }

        if (EXPLICIT_AUTH_ACTION_PATTERN.test(err.url)) {
          // Only clear if nothing else replaced this identity since — a failed attempt to
          // sign in as someone else must not wipe an unrelated, already-valid session
          const sentAccessToken = authReq.headers.get('access-token');
          const sentAuthorization = authReq.headers.get('Authorization');
          identity.getStoredHeaders().then((currentHeaders) => {
            const effectiveCurrent: Record<string, string> = {
              ...identity.defaultHeaders,
              ...currentHeaders,
            };
            const identityUnchanged =
              (effectiveCurrent['access-token'] ?? null) === sentAccessToken &&
              effectiveCurrent['Authorization'] === sentAuthorization;
            if (identityUnchanged) {
              identity.clearIdentity().then();
            }
          });
          return throwError(() => identity.errorHandler(err));
        }

        if (MICROSITE_SESSION_CHECK_PATTERN.test(err.url)) {
          return throwError(() => identity.errorHandler(err));
        }

        // Devise Token Auth rotates the access-token on every authenticated response
        // A request already in flight when that happens is still carrying the old token
        // The backend then rejects that old token with 401, even though the session is fine
        // So a 401 here isn't trusted on its own — see the retry block below for how we check
        // This request's token may already be stale — another request could have rotated it in between
        return from(identity.getStoredHeaders()).pipe(
          // Resend the same request with whatever token is stored right now, not the old one that just failed
          switchMap((freshHeaders) => {
            // Strip the old Devise headers first — setHeaders alone would leave a cleared/changed one stale
            const retryHeaders = DEVISE_IDENTITY_HEADER_KEYS.reduce(
              (headers, key) => headers.delete(key),
              authReq.headers,
            );

            return next(
              authReq.clone({
                headers: retryHeaders,
                setHeaders: { ...identity.defaultHeaders, ...freshHeaders },
              }),
            ).pipe(
              // Retry succeeded — store its token like any normal response would
              map((event) => {
                if (event instanceof HttpResponse) {
                  identity.updateIdentityFromResponse(event.headers, req.url);
                }
                return event;
              }),
              // Retry failed too — only log out if there really was a token, and it's still the same one
              catchError((retryErr) => {
                if (retryErr.status === 401) {
                  identity.getStoredHeaders().then((currentHeaders) => {
                    // "Nothing on either side" means there was never an identity to begin
                    // with, not a confirmed expiry — don't treat absence as expiry
                    const hadToken =
                      Boolean(freshHeaders['access-token']) ||
                      Boolean(freshHeaders['Authorization']);
                    const tokenUnchanged =
                      currentHeaders['access-token'] ===
                        freshHeaders['access-token'] &&
                      currentHeaders['Authorization'] ===
                        freshHeaders['Authorization'];

                    if (hadToken && tokenUnchanged) {
                      // This is the actual logout: clears the stored token, then redirects
                      identity
                        .clearIdentity()
                        .then(() => identity.redirectOnExpire());
                    }
                  });
                }
                return throwError(() => identity.errorHandler(retryErr));
              }),
            );
          }),
        );
      }),
    );

  // Public endpoints use only default headers — no identity headers sent
  if (PUBLIC_URL_PATTERNS.some((pattern) => req.url.includes(pattern))) {
    return handleResponse(
      req.clone({
        setHeaders: identity.defaultHeaders,
        withCredentials: req.withCredentials,
      }),
      true,
    );
  }

  return from(identity.getStoredHeaders()).pipe(
    switchMap((storedHeaders) => {
      // This branch mirrors the old cleanedParams logic, but HttpParams.get() only ever
      // returns a real string for a key that .keys() reports (TrackingClient itself already
      // filters out falsy params before building the request) — so this is a no-op by
      // construction in both the old and new code, not a behavior change. Left as-is.
      let params = req.params;
      if (req.url.includes('tracking')) {
        req.params.keys().forEach((key) => {
          if (req.params.get(key) === undefined) params = params.delete(key);
        });
      }

      // Apply identity headers (auth + language) to ALL non-public requests.
      // Old code always used requestOptions.headers regardless of withCredentials flag.
      const authReq = req.clone({
        setHeaders: {
          ...identity.defaultHeaders,
          ...storedHeaders,
        },
        withCredentials: req.withCredentials,
        params,
      });

      return handleResponse(authReq, false);
    }),
  );
};
